import React, { useState, useEffect } from 'react';
import logger from "../utils/logger";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Form, Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

// Providers
import { useTheme } from './themeControl/ThemeProvider';
import { useAlert } from '../providers/AlertProvider';
import { useAuthProvider } from '../providers/AuthProvider';

import { ActivityIndicator } from '../components/custom/ActivityIndicator';
import { validSession } from '../components/lib/validSession';
import UserEditModal from '../components/custom/UserEditModal';
import { downloadCSV, sortList } from './lib/functions';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faAnglesLeft, faAnglesRight, faIdCard, faUserShield, faWrench, faBuilding, faWarehouse, faBan, faUserPlus } from '@fortawesome/free-solid-svg-icons';

// Refactored Functions
import { DriversPageProfileFunctions } from '../functions/drivers_page/drivers_page_profile_functions';
import { DriversPageAccountCreationFunctions } from '../functions/drivers_page/drivers_page_account_creation_functions';

// import css
import './css/GlobalStyles.css';
import './css/DriversStyles.css';
import './css/LeftNavigation.css';
import './css/Styles.css';

// import images
import './img/light-driver.png';
import './img/dark-driver.png';
import './img/light-admin.png';
import './img/dark-admin.png';
import './img/light-service.png';
import './img/dark-service.png';
import './img/edit.png';
import './img/padlock.png';
import './img/unlock.png';
import './img/passwordReset.png';
import './img/light-customer.png';
import './img/dark-customer.png';
import './img/light-warehouse.png';
import './img/dark-warehouse.png';
import './img/2fa.png';

const DriversPage = () => {
    const { theme } = useTheme();
    const { showAlert } = useAlert();
    const { logout, isAdmin, isDev, isManager, features, permissions } = useAuthProvider();

    const canEdit = isDev || permissions.perm_users_edit;

    const navigate  = useNavigate();

    // Initialize function classes
    const profileFunctions = new DriversPageProfileFunctions(showAlert);
    const accountCreationFunctions = new DriversPageAccountCreationFunctions(showAlert);

    // loading state
    const [isLoading, setIsLoading] = useState(false);

    // Images
    const driver = require(`./img/${theme}-driver.png`);
    const admin = require(`./img/${theme}-admin.png`);
    const service = require(`./img/${theme}-service.png`);
    const customer = require(`./img/${theme}-customer.png`);
    const warehouse = require(`./img/${theme}-warehouse.png`);
    const edit = require(`./img/edit.png`);
    const padlock = require(`./img/padlock.png`);
    const unlock = require(`./img/unlock.png`);
    const passwordReset = require(`./img/passwordReset.png`);
    const reset2FA = require(`./img/2fa.png`);

    // ============================================================================================================================
    // ===========================================================[ STATES ]=======================================================
    // ============================================================================================================================

    // State to manage active tab
    const [activeTab, setActiveTab] = useState('drivers');
    const [showLeftNav, setShowLeftNav] = useState(false);

    // States to manage selected profile
    const [selectedAccount, setSelectedAccount] = useState(null);

    // State for account lists
    const [driversList, setDrivers] = useState([]);
    const [adminList, setAdmins] = useState([]);
    const [serviceList, setServices] = useState([]);
    const [customerProfiles, setCustomerProfiles] = useState([]);
    const [warehouseStaff, setWarehouseStaff] = useState([]);
    const [inactiveList, setInactive] = useState([]);
    const [customers, setCustomers] = useState([]);

    // State for all vehicles
    const [allVehicles, setAllVehicles] = useState([]);

    // date check states
    const [licenceOverdue, setLicenceOverdue] = useState([]);
    const [licenceDue, setLicenceDue] = useState([]);

    const [type, setType] = useState('DRIVER');

    // User edit modal ============================================================================================================
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOpenModal = () => {
        logger.log('Opening Modal');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    // Reset password ============================================================================================================
    const handleResetPassword = async () => {
        profileFunctions.handleResetPassword(selectedAccount, setIsLoading, setSelectedAccount, setActiveTab, fetchProfiles);
    };

    // Reset 2FA (dev only) ======================================================================================================
    const handleResetTotp = async () => {
        profileFunctions.handleResetTotp(selectedAccount, setIsLoading, showAlert);
    };

    // Update account ==================================================================================================
    const handleUpdate = async (updatedData) => {
        profileFunctions.handleUpdate(updatedData, setIsLoading, setSelectedAccount, setActiveTab, fetchProfiles);
    };


    // function to fetch profiles from backend ========================================================================================
    const fetchProfiles = async () => {
        profileFunctions.fetchProfiles(setIsLoading, setDrivers, setAdmins, setServices, setCustomerProfiles, setWarehouseStaff, setInactive, setLicenceOverdue, setLicenceDue, logout, navigate);
    };

    // Fetch vehicles from backend ==================================================================================================
    const fetchVehicles = async () => {
        profileFunctions.fetchVehicles(setAllVehicles);
    }

    // fetch customers from backend ==================================================================================================
    const fetchCustomers = async () => {
        profileFunctions.fetchCustomers(setCustomers);
    };

    // fetch data from backend on component mount
    useEffect(() => {
        fetchProfiles();
        fetchVehicles();
        fetchCustomers();
    }, []);


    // Functions to create account ==================================================================================================
    const {register, handleSubmit, reset, formState:{errors}} = useForm();

    const submitForm = (data) => {
        accountCreationFunctions.submitForm(data, type, setIsLoading, reset);
    };

    // Function to suspend account ==================================================================================================
    const handleSuspend = async (account) => {
        profileFunctions.handleSuspend(account, setIsLoading, setSelectedAccount, setActiveTab, fetchProfiles);
    }

    // check if date expires in next 28 days
    const checkDate = (expiryDate) => {
        return profileFunctions.checkDate(expiryDate);
    }

    // RESTRICT ACCESS ============================================================================================================
    if (!isAdmin && !isDev && !permissions.perm_users_view) {
        return (
            <div style={{ textAlign: 'center', marginTop: '30vh', height: '100vh', color: 'red' }}>
                <h3>Access Denied</h3>
                <p>You do not have permission to view this page</p>
            </div>
        )
    }

    return (
        <div id={theme} className="container-fluid closed-drivers-container">

            <Row>
                {/* Left Navigation Panel ============================================================================================*/}
                <Col className={showLeftNav ? "left-nav" : "closed-nav"} style={{ height: '100vh', paddingBottom: '150px' }} onMouseEnter={() => setShowLeftNav(true)} onMouseLeave={() => setShowLeftNav(false)}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%' }}>
                        {features.routing !== false && (<li title="Drivers" className={`nav-item ${activeTab === 'drivers' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'drivers' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('drivers'); setSelectedAccount(null);} : null}><FontAwesomeIcon icon={faIdCard} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Drivers</span></li>)}
                        <li title="Admin" className={`nav-item ${activeTab === 'admin' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'admin' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('admin'); setSelectedAccount(null);} : null}><FontAwesomeIcon icon={faUserShield} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Admin</span></li>
                        <li title="Service" className={`nav-item ${activeTab === 'service' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'service' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('service'); setSelectedAccount(null);} : null}><FontAwesomeIcon icon={faWrench} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Service</span></li>
                        {features.routing !== false && (<li title="Customers" className={`nav-item ${activeTab === 'customer' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'customer' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('customer'); setSelectedAccount(null);} : null}><FontAwesomeIcon icon={faBuilding} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Customers</span></li>)}
                        {features.warehouse !== false && (<li title="Warehouse" className={`nav-item ${activeTab === 'warehouse' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'warehouse' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('warehouse'); setSelectedAccount(null);} : null}><FontAwesomeIcon icon={faWarehouse} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Warehouse</span></li>)}
                        <li title="Inactive" className={`nav-item ${activeTab === 'inactive' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'inactive' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('inactive'); setSelectedAccount(null);} : null}><FontAwesomeIcon icon={faBan} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Inactive</span></li>
                        <li title="Add New Account" className={`nav-item ${activeTab === 'addAccount' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : canEdit ? 'pointer' : 'default', background: activeTab === 'addAccount' ? 'rgba(25, 151, 235, 0.8)' : 'transparent', opacity: canEdit ? 1 : 0.4 }} onClick={!isLoading && canEdit ? () => {setActiveTab('addAccount'); setSelectedAccount(null);} : null}><FontAwesomeIcon icon={faUserPlus} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Add New Account</span></li>
                    </ul>
                    {showLeftNav && (<>
                    {/* Drivers List */}
                    {activeTab === 'drivers' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Driver Accounts</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(driversList)}} />
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {driversList.map((driver) => (
                                    <li
                                        key={driver.id}
                                        style={{
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: 5,
                                            opacity: 1,
                                            border: selectedAccount?.id === driver.id ? '1px solid #0588f3' : 'transparent',
                                            backgroundColor: licenceOverdue.includes(driver.id)
                                            ? theme === 'light'
                                                ? 'rgba(247, 138, 138, 0.8)' // Light theme with defects
                                                : 'rgba(245, 105, 105, 0.29)' // Dark theme with defects
                                            : licenceDue.includes(driver.id)
                                                ? theme === 'light'
                                                    ? 'rgba(240, 247, 138, 0.80)' // Light theme with defects
                                                    : 'rgba(209, 195, 4, 0.2)' // Dark theme with defects
                                                : null
                                        }}
                                        onClick={!isLoading ? () => setSelectedAccount(driver) : null}
                                    >
                                        {driver.name} {driver.surname}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                        
                    {/* Admin List */}
                    {activeTab === 'admin' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Admin Accounts</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(adminList)}} />
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {adminList.map((admin) => (
                                    <li
                                        key={admin.id}
                                        style={{
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: 5,
                                            opacity: 1,
                                            border: selectedAccount?.id === admin.id ? '1px solid #0588f3' : 'transparent',
                                            backgroundColor: licenceOverdue.includes(admin.id)
                                            ? theme === 'light'
                                                ? 'rgba(247, 138, 138, 0.8)' // Light theme with defects
                                                : 'rgba(245, 105, 105, 0.29)' // Dark theme with defects
                                            : licenceDue.includes(admin.id)
                                                ? theme === 'light'
                                                    ? 'rgba(240, 247, 138, 0.80)' // Light theme with defects
                                                    : 'rgba(209, 195, 4, 0.2)' // Dark theme with defects
                                                : null
                                        }}
                                        onClick={!isLoading ? () => setSelectedAccount(admin) : null}
                                    >
                                        {admin.name} {admin.surname}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Service List */}
                    {activeTab === 'service' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Service Accounts</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(serviceList)}} />
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {serviceList.map((service) => (
                                    <li
                                        key={service.id}
                                        style={{
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: 5,
                                            opacity: 1,
                                            border: selectedAccount?.id === service.id ? '1px solid #0588f3' : 'transparent'
                                        }}
                                        onClick={!isLoading ? () => setSelectedAccount(service) : null}
                                    >
                                        {service.name}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Customer List */}
                    {activeTab === 'customer' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Customer Accounts</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(customerProfiles)}} />
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {customerProfiles.map((customer) => (
                                    <li
                                        key={customer.id}
                                        style={{
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: 5,
                                            opacity: 1,
                                            border: selectedAccount?.id === customer.id ? '1px solid #0588f3' : 'transparent'
                                        }}
                                        onClick={!isLoading ? () => setSelectedAccount(customer) : null}
                                    >
                                        {customer.name} {customer.surname}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Warehouse List */}
                    {activeTab === 'warehouse' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Warehouse Staff</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(warehouseStaff)}} />
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {warehouseStaff.map((staff) => (
                                    <li
                                        key={staff.id}
                                        style={{
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: 5,
                                            opacity: 1,
                                            border: selectedAccount?.id === staff.id ? '1px solid #0588f3' : 'transparent'
                                        }}
                                        onClick={!isLoading ? () => setSelectedAccount(staff) : null}
                                    >
                                        {staff.name} {staff.surname}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Inactive List */}
                    {activeTab === 'inactive' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Inactive Accounts</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(inactiveList)}} />
                            </div>

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {inactiveList.map((inactive) => (
                                    <li
                                        key={inactive.id}
                                        style={{
                                            cursor: isLoading ? 'not-allowed' : 'pointer',
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: 5,
                                            opacity: 1,
                                            border: selectedAccount?.id === inactive.id ? '1px solid #0588f3' : 'transparent'
                                        }}
                                        onClick={!isLoading ? () => setSelectedAccount(inactive) : null}
                                    >
                                        {inactive.name} {inactive.surname}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    </>)}
                </Col>

                {/* Right Content Panel ===================================================================================================================*/}
                <Col className="closed-nav-content" style={{ padding: '20px' }}>

                {isLoading ? (
                            <div style={{ alignSelf: 'center', justifyContent: 'center', marginTop: '30vh' }}>
                                <ActivityIndicator />
                            </div>
                    ) : (
                    <>
                    {/* Edit User Data Modal */}
                    {isModalOpen && (
                        <UserEditModal
                            theme={theme}
                            user={selectedAccount}
                            isOpen={isModalOpen}
                            onClose={handleCloseModal}
                            onSubmit={handleUpdate}
                            group={type}
                            vehicles={allVehicles}
                            canEdit={canEdit}
                        />                        
                    )}


                    <div className="accounts-conatiner" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {/* Right Content Drivers */}
                        {activeTab !== 'addAccount' && (
                            <>

                            {/* Header */}
                            <span style={{ textAlign: 'center'}}>
                            {(() => {
                                switch (activeTab) {
                                    case 'drivers':
                                        return <h3>Driver Information</h3>;
                                    case 'admin':
                                        return <h3>Admin Information</h3>;
                                    case 'service':
                                        return <h3>Service Information</h3>;
                                    case 'customer':
                                        return <h3>Customer Information</h3>;
                                    case 'warehouse':
                                        return <h3>Warehouse Information</h3>;
                                    case 'inactive':
                                        return <h3>Inactive Information</h3>;
                                    default:
                                        return null; // Handle unexpected cases
                                }
                            })()}

                            {/* edit/view account buttons */}
                            {selectedAccount && (
                                <div className="accounts-buttons">
                                    {canEdit ? (
                                        <span className="type-button" onClick={() => handleOpenModal()}><img src={edit} width={110} height={110} alt="edit-img" same-site="true"></img>Edit</span>
                                    ) : (
                                        <span className="type-button" onClick={() => handleOpenModal()}><img src={edit} width={110} height={110} alt="view-img" same-site="true"></img>View Details</span>
                                    )}
                                    {canEdit && (
                                        <>
                                            {activeTab !== 'inactive' ? (
                                                <span className="type-button" onClick={() => handleSuspend(selectedAccount)}><img src={padlock} width={110} height={110} alt="suspend-img" same-site="true"></img>Suspend</span>
                                            ) : (
                                                <span className="type-button" onClick={() => handleSuspend(selectedAccount)}><img src={unlock} width={110} height={110} alt="activate-img" same-site="true"></img>Activate</span>
                                            )}
                                            {isDev && (
                                                <span className="type-button" onClick={() => handleResetPassword()}><img src={passwordReset} width={110} height={110} alt="edit-img" same-site="true"></img>Reset Password</span>
                                            )}
                                            {isDev && (
                                                <span className="type-button" onClick={() => handleResetTotp()}><img src={reset2FA} width={110} height={110} alt="reset-2fa-img" same-site="true"></img>Reset 2FA</span>
                                            )}
                                        </>
                                    )}
                                </div>                            
                            )}

                            {/* Account Details */}
                            </span>
                                {selectedAccount ? (
                                    <div style={{ maxWidth: 600, margin: '20px auto', padding: '0 20px', textAlign: 'center' }}>
                                        <p><strong>Name:</strong> {selectedAccount.name} {selectedAccount.surname}</p>
                                        <p><strong>Email:</strong> {selectedAccount.email}</p>
                                        <p><strong>Phone:</strong> {selectedAccount.phone}</p>
                                        {(selectedAccount.group !== 'CUSTOMER' && selectedAccount.group !== 'SERVICE') && <p><strong>Pin:</strong> {selectedAccount.pin}</p>}
                                        <p><strong>Address:</strong> {selectedAccount.address}</p>

                                        {/* Holiday Entitlement */}
                                        {(selectedAccount.group !== 'SERVICE' && selectedAccount.group !== 'CUSTOMER') && (
                                            <p><strong>Holiday Entitlement:</strong> {selectedAccount.holidays}</p>
                                        )}
                                        
                                        {/* Customer-specific fields */}
                                        {selectedAccount.group === 'CUSTOMER' && selectedAccount.customer_id && (
                                            <p>
                                                <strong>Customer Company:</strong>{' '}
                                                {customers.find(customer => customer.id === selectedAccount.customer_id)?.company || 'Customer not found'}
                                            </p>
                                        )}

                                        {/* Driver/Admin/Warehouse specific fields - hide for customers and service */}
                                        {selectedAccount.group !== 'CUSTOMER' && selectedAccount.group !== 'SERVICE' && (
                                            <>
                                                {selectedAccount.licence && (
                                                    <>
                                                    <p><strong>Licence No:</strong> {selectedAccount.licence}</p>
                                                    <p style={{ color: checkDate(selectedAccount.licence_expiry) ? 'red' : null }}><strong>Licence Exp:</strong> {selectedAccount.licence_expiry ? new Date(selectedAccount.licence_expiry).toLocaleDateString('en-GB') : null}</p>
                                                    </>
                                                )}
                                                {selectedAccount.tacho && (
                                                    <>
                                                    <p><strong>Tacho No:</strong> {selectedAccount.tacho}</p>
                                                    <p style={{ color: checkDate(selectedAccount.tacho_expiry) ? 'red' : null }}><strong>Tacho Exp:</strong> {selectedAccount.tacho_expiry ? new Date(selectedAccount.tacho_expiry).toLocaleDateString('en-GB') : null}</p>
                                                    </>
                                                )}
                                                {selectedAccount.cpc_expiry && (
                                                    <p style={{ color: checkDate(selectedAccount.cpc_expiry) ? 'red' : null }}><strong>CPC Exp:</strong> {selectedAccount.cpc_expiry ? new Date(selectedAccount.cpc_expiry).toLocaleDateString('en-GB') : null}</p>
                                                )}
                                                {selectedAccount.adr_expiry && (
                                                    <p><strong>ADR Exp:</strong> {selectedAccount.adr_expiry ? new Date(selectedAccount.adr_expiry).toLocaleDateString('en-GB') : null}</p>
                                                )}
                                                {selectedAccount.truck_id && (
                                                <p>
                                                    <strong>Vehicle:</strong>{' '}
                                                    {selectedAccount.truck_id
                                                        ? allVehicles?.find((vehicle) => vehicle.id === selectedAccount.truck_id)?.reg || 'Vehicle not found'
                                                        : 'No vehicle assigned'}
                                                </p>
                                                )}
                                            </>
                                        )}

                                        <span style={{color: selectedAccount.status === "active" ? 'green' : 'red'}}>
                                            <p><strong>Status:</strong> {selectedAccount.status}</p>
                                        </span>

                                        <span style={{color: 'goldenrod'}}>
                                            <p><strong>2FA:</strong> {selectedAccount.totp_enabled ? 'Enabled' : 'Not set'}</p>
                                        </span>

                                    </div>
                                ) : (
                                    <span style={{ textAlign: 'center'}}>
                                        {(() => {
                                            switch (activeTab) {
                                                case 'drivers':
                                                    return <p>Please select a driver account from the list.</p>;
                                                case 'admin':
                                                    return <p>Please select an admin account from the list.</p>;
                                                case 'service':
                                                    return <p>Please select a service provider from the list.</p>;
                                                case 'customer':
                                                    return <p>Please select a customer account from the list.</p>;
                                                case 'warehouse':
                                                    return <p>Please select a warehouse staff member from the list.</p>;
                                                case 'inactive':
                                                    return <p>Please select an account from the list.</p>;
                                                default:
                                                    return null; // Handle unexpected cases
                                            }
                                        })()}
                                    </span>
                                )}
                            </>
                        )}
                    </div>

                    {/* Right Content Add Account */}
                    {activeTab === 'addAccount' && (
                        <>
                            <span style={{ textAlign: 'center' }}>
                                <h3>Add new account</h3>
                            </span>

                            {/* account type selection */}
                            <div style={{ textAlign: 'center', flexDirection: 'row' }}>
                                {features.routing !== false && (
                                <span className="type-button" onClick={() => setType('DRIVER')} style={{ 
                                    border: type === "DRIVER" ? '3px solid #0588f3' : '3px solid transparent',
                                    backgroundColor: type === "DRIVER" ? 'rgba(5, 136, 243, 0.1)' : 'transparent'
                                }}><img src={driver} width={110} height={110} alt="driver-img" same-site="true"></img>Driver</span>
                                )}
                                <span className="type-button" onClick={() => setType('ADMIN')} style={{ 
                                    border: type === "ADMIN" ? '3px solid #0588f3' : '3px solid transparent',
                                    backgroundColor: type === "ADMIN" ? 'rgba(5, 136, 243, 0.1)' : 'transparent'
                                }}><img src={admin} width={110} height={110} alt="driver-img" same-site="true"></img>Admin</span>
                                <span className="type-button" onClick={() => setType('SERVICE')} style={{ 
                                    border: type === "SERVICE" ? '3px solid #0588f3' : '3px solid transparent',
                                    backgroundColor: type === "SERVICE" ? 'rgba(5, 136, 243, 0.1)' : 'transparent'
                                }}><img src={service} width={110} height={110} alt="driver-img" same-site="true"></img>Service</span>
                                {features.warehouse !== false && (
                                <span className="type-button" onClick={() => setType('WAREHOUSE')} style={{ 
                                    border: type === "WAREHOUSE" ? '3px solid #0588f3' : '3px solid transparent',
                                    backgroundColor: type === "WAREHOUSE" ? 'rgba(5, 136, 243, 0.1)' : 'transparent'
                                }}><img src={warehouse} width={110} height={110} alt="warehouse-img" same-site="true"></img>Warehouse</span>
                                )}
                                {features.routing !== false && (
                                <span className="type-button" onClick={() => setType('CUSTOMER')} style={{ 
                                    border: type === "CUSTOMER" ? '3px solid #0588f3' : '3px solid transparent',
                                    backgroundColor: type === "CUSTOMER" ? 'rgba(5, 136, 243, 0.1)' : 'transparent'
                                }}><img src={customer} width={110} height={110} alt="customer-img" same-site="true"></img>Customer</span>
                                )}
                            </div>
                            
                            <>
                                <form>

                                {/* account type */}
                                <span style={{ textAlign: 'center' }}>
                                    <h3>{type}</h3>
                                </span>

                                    {/*"""Name OR Company"""*/}
                                    <Form.Group className="signup-form">
                                        <Form.Label>{type === 'SERVICE' ? 'Company' : type === 'CUSTOMER' ? 'Company' : 'First Name'}</Form.Label>
                                        <Form.Control type="text"
                                            placeholder={type === 'SERVICE' ? 'Company' : type === 'CUSTOMER' ? 'Company' : 'First Name'}
                                            {...register('firstName', { required: true, maxLength: 25 })} /*Validates that a name is entered correctly*/ />
                                    </Form.Group>
                                    {/*"""Error handling"""*/}
                                    <div className="signup-error-container">
                                        {errors.firstName && <p className="signup-errors"><small>{type === 'SERVICE' ? 'Company' : type === 'CUSTOMER' ? 'Contact Name' : 'First Name'} is required</small></p>}
                                        {errors.firstName?.type === "maxLength" && <p className="signup-errors"><small>Max length 25 characters</small></p>}
                                    </div>

                                    {/*"""Surname IF NOT SERVICE OR CUSTOMER"""*/}
                                    {type !== "SERVICE" && type !== "CUSTOMER" && (
                                        <>
                                            <Form.Group className="signup-form">
                                                <Form.Label>Surname</Form.Label>
                                                <Form.Control type="text"
                                                    placeholder="Surname"
                                                    {...register('surname', { required: true, maxLength: 15 })} /*Validates that a name is entered correctly*/ />
                                            </Form.Group>

                                            <div className="signup-error-container">
                                                {errors.surname && <p className="signup-errors"><small>Surname is required</small></p>}
                                                {errors.surname?.type === "maxLength" && <p className="signup-errors"><small>Max length 15 characters</small></p>}
                                            </div>
                                        </>
                                    )}

                                    {/*"""Create E-mail"""*/}
                                    <Form.Group className="signup-form">
                                        <Form.Label>E-mail</Form.Label>
                                        <Form.Control type="email"
                                            placeholder="Enter E-mail"
                                            {...register('email', { required: true, maxLength: 80 })} />
                                    </Form.Group>
                                    <div className="signup-error-container">
                                        {errors.email && <p className="signup-errors"><small>Email is required</small></p>}
                                        {errors.email?.type === "maxLength" && <p className="signup-errors"><small>Max length 80 characters</small></p>}
                                    </div>

                                    {/*"""Customer Selection IF CUSTOMER TYPE"""*/}
                                    {type === "CUSTOMER" && (
                                        <>
                                            <Form.Group className="signup-form">
                                                <Form.Label>Select Customer Company</Form.Label>
                                                <Form.Control as="select"
                                                    {...register('customer_id', { required: true })}
                                                >
                                                    <option value="">-- Select Customer --</option>
                                                    {customers.map((customer) => (
                                                        <option key={customer.id} value={customer.id}>
                                                            {customer.company}
                                                        </option>
                                                    ))}
                                                </Form.Control>
                                            </Form.Group>
                                            <div className="signup-error-container">
                                                {errors.customer_id && <p className="signup-errors"><small>Customer selection is required</small></p>}
                                            </div>
                                        </>
                                    )}

                                    {/*"""Create Password"""*/}
                                    <Form.Group className="signup-form">
                                        <Form.Label>Password</Form.Label>
                                        <Form.Control type="password"
                                            placeholder="Enter Password"
                                            {...register('password', { required: true, minLength: 8 })} />
                                    </Form.Group>
                                    <div className="signup-error-container">
                                        {errors.password && <p className="signup-errors"><small>Password is required</small></p>}
                                        {errors.password?.type === "minLength" && <p className="signup-errors"><small>Min length 8 characters</small></p>}
                                    </div>

                                    {/*"""Confirm Password"""*/}
                                    <Form.Group className="signup-form">
                                        <Form.Label>Confirm Password</Form.Label>
                                        <Form.Control type="password"
                                            placeholder="Confirm Password"
                                            {...register('confirmPassword', { required: true, minLength: 8 })} />
                                    </Form.Group>
                                    <div className="signup-error-container">
                                        {errors.confirmPassword && <p className="signup-errors"><small>Confirmation is required</small></p>}
                                        {errors.confirmPassword?.type === "minLength" && <p className="signup-errors"><small>Min length 8 characters</small></p>}
                                    </div>

                                    {/*"""Submit Button"""*/}
                                    <Form.Group>
                                        <div className="signup-button-container">
                                            <Button as="sub" variant="primary" onClick={handleSubmit(submitForm)}>SignUp</Button>
                                        </div>
                                    </Form.Group>

                                </form>
                            </>
                        </>
                    )}
                    </>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default DriversPage;
